import { Modal, useModals } from 'almanac-ui/Modal'
import { getMediaFiles } from 'components/Design/UploadsTab/getMediaFiles'
import { useUploads } from 'components/Design/UploadsTab/useUploads'
import { collectResults } from 'utils/query'
import { ScrollContainer } from '../ScrollContainer/ScrollContainer'
import InfiniteScroll from 'common/InfiniteScroll/InfiniteScroll'
import Styles from './Uploads.module.scss'
import { Text } from 'blaze-ui/Text/Text'
import useResponsiveDesign from 'hooks/useResponsiveDesign'
import { MediaFileThumbnail } from '../MediaFileThumbnail/MediaFileThumbnail'
import { ImagePickerProviderProps } from 'components/ImagePickerModal/ImagePickerContextTypes'
import { ImagePickerProvider, useImagePickerContext } from 'components/ImagePickerModal/ImagePickerContext'
import { Button } from 'blaze-ui/Button'
import { isVideo } from 'components/ImagePickerModal/utils'

interface Props extends Omit<ImagePickerProviderProps, 'children'> {}

const AllUploadsContainer = (props: Props) => (
  <ImagePickerProvider {...props}>
    <AllUploads />
  </ImagePickerProvider>
)

export const AllUploadsContent = () => {
  const uploadsQuery = useUploads()
  const uploads = collectResults(uploadsQuery, (data) => data.files)
  const context = useImagePickerContext()
  const { selectedImages, toggleSelectedImage, accept } = context
  const mediaFiles = getMediaFiles(uploads, accept)
  const { hasNextPage, fetchNextPage } = uploadsQuery
  const { isMobile } = useResponsiveDesign()

  return (
    <div className={Styles.allUploads} data-testid="upload-provider">
      <ScrollContainer>
        <div className={Styles.allUploadsGrid}>
          {mediaFiles.map((mediaFile) => {
            const url = mediaFile.source_url || mediaFile.thumbnail_url

            return (
              <div key={mediaFile.id} className={Styles.allUploadsItem}>
                <MediaFileThumbnail
                  mediaFile={{ ...mediaFile, url: url as string }}
                  onToggleSelect={toggleSelectedImage}
                  isSelected={selectedImages.some((i) => i.url === url)}
                  duration={mediaFile.duration}
                />
                {!isMobile && <Text variant="smallList">{mediaFile.title}</Text>}
              </div>
            )
          })}
        </div>
        {hasNextPage && (
          <div className={Styles.infiniteLoader}>
            <InfiniteScroll hasMore={hasNextPage} loadMore={fetchNextPage} />
          </div>
        )}
      </ScrollContainer>
    </div>
  )
}

const AllUploads = () => {
  const context = useImagePickerContext()
  const { closeModal } = useModals()
  const { selectedImages, onConfirm } = context

  const isSelectedVideo = isVideo({ url: selectedImages[0]?.source_url, type: selectedImages[0]?.mime_type })

  const handleConfirm = () => {
    onConfirm(selectedImages, context)
  }

  return (
    <Modal.Root size="lg" data-testid="all-uploads" onPressOutside={closeModal}>
      <Modal.Header
        title={
          <>
            <Modal.BackButton />
            Uploaded Media
          </>
        }
        actions={
          <div className={Styles.allUploadsActions}>
            {selectedImages.length > 0 && (
              <Button onPress={handleConfirm} size="lg">
                {isSelectedVideo ? 'Select Video' : 'Select Image'}
              </Button>
            )}
          </div>
        }
      />
      <AllUploadsContent />
    </Modal.Root>
  )
}

export { AllUploadsContainer as AllUploads }
